import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { BRAND } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "How do I reduce Medicaid pending past 30 days?",
  "What should I audit before an annual survey?",
  "Common social services documentation mistakes?",
  "How does AR aging signal operational risk?",
];

const WELCOME = `Hello. I'm AthenaCrest AI, the digital extension of Sierra Thomas's consulting practice.

You can ask me about Medicaid pending workflows, survey readiness, AR recovery, social services documentation, discharge planning, operational risk, and other long-term care challenges your facility may be navigating.

What is on your mind today?`;

export default function AskAIChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: WELCOME },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const userMsg = (text ?? input).trim();
    if (!userMsg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMsg,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I'm having trouble reaching the AthenaCrest assistant right now. Please try again in a moment, or book a consultation through the Contact page.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[10000] flex items-center gap-2 bg-teal text-ivory pl-4 pr-5 py-3 rounded-full shadow-[0_12px_32px_-8px_rgba(16,79,85,0.6)] hover:bg-navy transition-all duration-300 group border border-teal hover:border-gold"
        data-testid="ask-ai-launcher"
        aria-label="Ask AthenaCrest AI"
      >
        <div className="relative">
          <Sparkles className="h-4 w-4 text-gold group-hover:animate-flame-flicker" />
        </div>
        <span className="font-sans text-sm tracking-wide hidden sm:inline">
          Ask AthenaCrest AI
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 bg-ivory border-l border-champagne-200 flex flex-col"
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <SheetHeader className="bg-navy text-ivory px-6 py-5 border-b border-gold/30 space-y-1">
            <SheetTitle className="text-ivory font-serif text-2xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold" /> Ask AthenaCrest AI
            </SheetTitle>
            <SheetDescription className="text-ivory/70 font-sans text-xs">
              Operational & compliance insight, powered by AI. Not legal advice.
            </SheetDescription>
          </SheetHeader>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-6 space-y-4 bg-paper"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 font-sans text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-navy text-ivory rounded-l-lg rounded-tr-lg"
                      : "bg-ivory border border-champagne-200 text-navy rounded-r-lg rounded-tl-lg"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-ivory border border-champagne-200 text-navy/60 px-4 py-3 rounded-r-lg rounded-tl-lg font-sans text-sm italic flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                  Thinking…
                </div>
              </div>
            )}

            {messages.length === 1 && !loading && (
              <div className="pt-4">
                <div className="overline text-[10px] mb-3">Try asking</div>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-left text-sm font-sans text-navy/85 border border-champagne-200 hover:border-gold hover:bg-champagne-100 transition-colors px-3 py-2"
                      data-testid={`chat-suggestion-${s.slice(0, 12)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-champagne-200 p-4 bg-ivory flex gap-2"
            data-testid="chat-form"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Medicaid pending, survey readiness, AR…"
              disabled={loading}
              className="flex-1 bg-champagne-100 border border-champagne-200 px-4 py-3 font-sans text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-gold"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-navy text-ivory px-4 py-3 hover:bg-teal transition-colors disabled:opacity-40 disabled:hover:bg-navy"
              data-testid="chat-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
